import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  TextField,
  Button,
  Box,
  Card,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { API_ENDPOINTS } from "../../../config/api";

const API_URL = API_ENDPOINTS.PRODUCT_TYPES;

const EditProductTypeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [productType, setProductType] = useState({
    name: "",
    publish: false,
    pieces: false,
  });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success' | 'error'
  });
  const [redirectOnClose, setRedirectOnClose] = useState(false);

  useEffect(() => {
    fetchProductType();
  }, []);

  const fetchProductType = async () => {
    try {
      const response = await axios.get(API_URL.EDIT(id));
      setProductType({
        name: response.data.productType.name || "",
        publish: response.data.productType.publish || false,
        pieces: response.data.productType.pieces || false,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching product type:", error);
      setSnackbar({
        open: true,
        message: "Failed to load product type data",
        severity: "error",
      });
      setRedirectOnClose(false);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setProductType({ ...productType, [e.target.name]: e.target.value });
  };

  const handlePublishToggle = () => {
    setProductType((prev) => ({ ...prev, publish: !prev.publish }));
  };

  const handlePiecesToggle = () => {
    setProductType((prev) => ({ ...prev, pieces: !prev.pieces }));
  };

  const handleUpdate = async () => {
    try {
      await axios.put(API_URL.EDIT(id), productType);
      setSnackbar({
        open: true,
        message: "Product Type updated successfully!",
        severity: "success",
      });
      setRedirectOnClose(true);
    } catch (error) {
      console.error("Error updating product type:", error);
      setSnackbar({
        open: true,
        message: "Failed to update product type: " + (error.response?.data?.message || "Something went wrong"),
        severity: "error",
      });
      setRedirectOnClose(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
    if (redirectOnClose) {
      navigate(-1); // Go back
    }
  };

  if (loading) {
    return <CircularProgress sx={{ display: "block", margin: "auto", mt: 5 }} />;
  }

  return (
    <>
      <Card sx={{ p: 4, maxWidth: 500, mx: "auto", mt: 5 }}>
        <Typography variant="h5" mb={3}>
          Edit Product Type
        </Typography>
        <Box display="flex" flexDirection="column" gap={3}>
          <TextField
            label="Product Type Name"
            name="name"
            value={productType.name}
            onChange={handleChange}
            required
          />
          <FormControlLabel
            control={
              <Switch
                checked={productType.publish}
                onChange={handlePublishToggle}
                color="primary"
              />
            }
            label="Published"
          />
          <FormControlLabel
            control={
              <Switch
                checked={productType.pieces}
                onChange={handlePiecesToggle}
                color="primary"
              />
            }
            label="Pieces"
          />
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Button variant="contained" color="primary" onClick={handleUpdate}>
              Update
            </Button>
            <Button variant="outlined" color="secondary" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Card>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={1000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditProductTypeForm;
